import type { ToolActivityOutcome } from "@esi/shared";
import { useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Clock,
  Code,
  Download,
  File,
  ImageIcon,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatBytes } from "../i18n/format.ts";
import { useOpenArtefact } from "../router.tsx";
import {
  artefactDownloadHref,
  artefactPreviewHref,
  type ConversationScope,
  refreshArtefacts,
  useArtefacts,
} from "./artefacts/queries.ts";
import { type CodeRunFile, clipText, codeRunInputOf, codeRunResultOf } from "./code-run.ts";
import { type DecisionFailure, refusalOf, type ToolPart } from "./tool-approvals.ts";

/**
 * A `run_code` call in an answer (openspec change `agent-turn`, D20): the note and the outcome,
 * then on demand the program as a read-only block, its output and errors clipped to 2,000
 * characters with "Show all", and the files it produced as artefact cards with an inline preview
 * for images. A blocked or declined call says why. Everything is plain text: the program's
 * output is never rendered as Markdown or HTML, and an image is drawn from the API's preview
 * route only.
 */

/** Output and errors shown before "Show all" (D20). */
export const CODE_OUTPUT_SHOWN = 2_000;

const OUTCOME_ICON: Partial<Record<ToolActivityOutcome, typeof Check>> = {
  done: Check,
  blocked: Ban,
  declined: X,
  expired: Clock,
  failed: CircleAlert,
};

export function CodeRunRow(props: {
  part: ToolPart;
  outcome: ToolActivityOutcome;
  failure: DecisionFailure | undefined;
  /** Where its conversation lives: the files' routes. Absent: no file actions. */
  scope: ConversationScope | null;
  /** The names of the files given, from the approval (when it asked for one). */
  filesIn?: readonly string[] | undefined;
  /**
   * In the timeline's step: the files are named on one line, their cards (preview, Open,
   * Download) are in the answer only (user, 2026-11-03: never show a file twice).
   */
  filesAsNames?: boolean;
}) {
  const { part, failure, scope } = props;
  const { t } = useTranslation();
  const outcome: ToolActivityOutcome =
    props.outcome === "running" &&
    part.state === "approval-responded" &&
    part.approval?.approved === false
      ? "declined"
      : props.outcome;
  const input = codeRunInputOf(part.input);
  const result = outcome === "done" ? codeRunResultOf(part.output) : null;
  const refusal = refusalOf(part.output);
  const nonZero = result !== null && result.exitCode !== null && result.exitCode !== 0;
  const Icon = OUTCOME_ICON[outcome];
  const reasons = refusal?.reasons ?? [];
  const explanation: ReactNode = failure
    ? t(
        failure === "expired"
          ? "tools.expired"
          : failure === "decided"
            ? "tools.alreadyDecided"
            : "tools.unavailable",
      )
    : outcome === "declined"
      ? t("tools.code.declined")
      : outcome === "expired"
        ? t("tools.expired")
        : outcome === "failed"
          ? t("tools.code.failed")
          : outcome === "blocked"
            ? t("tools.code.blocked")
            : null;
  return (
    <div className="flex flex-col gap-1.5 text-xs" data-code-run="">
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-fg-muted">
        {input.note ? (
          <span className="min-w-0 text-[13px] break-words text-fg-secondary">{input.note}</span>
        ) : (
          <span className="text-fg-secondary">{t("tools.label.run_code")}</span>
        )}
        {outcome !== "running" && Icon ? (
          <>
            <span aria-hidden="true">·</span>
            <span
              className={cn(
                "inline-flex items-center gap-1 font-medium",
                outcome === "blocked" || outcome === "failed" || nonZero
                  ? "text-danger-fg"
                  : "text-fg-secondary",
              )}
            >
              {nonZero ? (
                <CircleAlert aria-hidden="true" className="size-3 shrink-0" strokeWidth={2} />
              ) : (
                <Icon aria-hidden="true" className="size-3 shrink-0" strokeWidth={2} />
              )}
              {nonZero
                ? t("tools.code.exit", { code: result?.exitCode })
                : t(`tools.outcome.${outcome}`)}
            </span>
          </>
        ) : null}
        {result && result.files.length > 0 ? (
          <>
            <span aria-hidden="true">·</span>
            <span className="tabular-nums">
              {t("tools.code.filesMade", { count: result.files.length })}
            </span>
          </>
        ) : null}
      </p>
      {explanation ? <p className="text-fg-secondary">{explanation}</p> : null}
      {outcome === "blocked" && reasons.length > 0 ? (
        <ul className="flex list-disc flex-col gap-0.5 pl-4 text-fg-secondary">
          {reasons.map((reason) => (
            <li key={reason}>{t(`tools.reason.${reason}`)}</li>
          ))}
        </ul>
      ) : null}
      {input.code ? (
        <CodeDetails
          code={input.code}
          language={input.language}
          stdout={result?.stdout ?? ""}
          stderr={result?.stderr ?? ""}
          filesIn={props.filesIn ?? []}
          dropped={result?.dropped ?? []}
          sent={outcome === "done" || outcome === "failed"}
        />
      ) : null}
      {result && result.files.length > 0 ? (
        props.filesAsNames ? (
          <p
            className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-fg-secondary"
            data-code-file-names=""
          >
            {result.files.map((f, i) => (
              <span key={`${f.artefact}:${f.version}`} className="inline-flex items-center gap-1.5">
                {i > 0 ? (
                  <span aria-hidden="true" className="text-fg-muted">
                    ·
                  </span>
                ) : null}
                <span className="break-all">{f.name}</span>
              </span>
            ))}
          </p>
        ) : (
          <CodeRunFiles files={result.files} scope={scope} />
        )
      ) : null}
    </div>
  );
}

/** "Show details": the program, its output and errors, the files given and dropped. */
function CodeDetails(props: {
  code: string;
  language: string | null;
  stdout: string;
  stderr: string;
  filesIn: readonly string[];
  dropped: readonly { name: string; reason: string }[];
  /** The program was sent to the sandbox. */
  sent: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded-sm font-medium text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus [&::-webkit-details-marker]:hidden">
        {open ? (
          <ChevronDown aria-hidden="true" className="size-3" strokeWidth={2} />
        ) : (
          <ChevronRight aria-hidden="true" className="size-3" strokeWidth={2} />
        )}
        {open ? t("conversation.timeline.hideDetails") : t("conversation.timeline.details")}
      </summary>
      <div className="mt-1.5 flex flex-col gap-2">
        <Block
          label={
            props.language
              ? t("tools.code.programIn", { language: props.language })
              : t("tools.code.program")
          }
          note={props.sent ? undefined : t("tools.arguments.notSent")}
          text={props.code}
        />
        {props.filesIn.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-fg-muted">{t("tools.code.filesIn")}</span>
            <p className="text-[13px] break-words text-fg-secondary">{props.filesIn.join(", ")}</p>
          </div>
        ) : null}
        {props.stdout ? <Clipped label={t("tools.code.output")} text={props.stdout} /> : null}
        {props.stderr ? <Clipped label={t("tools.code.errors")} text={props.stderr} /> : null}
        {props.dropped.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-fg-muted">{t("tools.code.dropped")}</span>
            <ul className="flex flex-col gap-0.5 text-fg-secondary">
              {props.dropped.map((d, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: names may repeat; order is stable
                <li key={i} className="break-words">
                  <span className="font-medium">{d.name}</span>
                  {" · "}
                  {t(`tools.code.dropReason.${dropReasonOf(d.reason)}`)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </details>
  );
}

const KNOWN_DROPS = [
  "type",
  "size",
  "count",
  "limit",
  "writes",
  "artefacts_off",
  "failed",
] as const;
const dropReasonOf = (reason: string): (typeof KNOWN_DROPS)[number] =>
  (KNOWN_DROPS as readonly string[]).includes(reason)
    ? (reason as (typeof KNOWN_DROPS)[number])
    : "failed";

/** A read-only monospace block under a quiet label. */
function Block(props: { label: string; text: string; note?: string | undefined }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-fg-muted">
        {props.label}
        {props.note ? ` · ${props.note}` : ""}
      </span>
      <pre className="max-h-80 overflow-auto rounded-md border border-line bg-surface-sunken px-2.5 py-2 font-mono text-[12.5px] leading-normal break-words whitespace-pre-wrap text-fg">
        {props.text}
      </pre>
    </div>
  );
}

/** Output or errors, clipped to {@link CODE_OUTPUT_SHOWN} characters until "Show all". */
function Clipped(props: { label: string; text: string }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const clipped = clipText(props.text, CODE_OUTPUT_SHOWN);
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <Block label={props.label} text={all ? props.text : clipped.text} />
      {clipped.cut > 0 ? (
        <button
          type="button"
          onClick={() => setAll((a) => !a)}
          className="w-fit font-medium text-accent-text underline-offset-4 hover:underline"
        >
          {all ? t("tools.code.showLess") : t("tools.code.showAll", { count: clipped.cut })}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The files a run kept, as artefact cards: an image previewed (from the API, PNG and JPEG), then
 * Open (beside the conversation) and Download (a URL minted per click). A file the viewer may not
 * read is not listed by the API, so its card says it is unavailable.
 */
export function CodeRunFiles(props: {
  files: readonly CodeRunFile[];
  scope: ConversationScope | null;
}) {
  const { scope } = props;
  return (
    <ul className="flex flex-col gap-2" data-code-files="">
      {props.files.map((f) => (
        <li key={`${f.artefact}:${f.version}`}>
          {scope ? <FileCard file={f} scope={scope} /> : <FileLine file={f} />}
        </li>
      ))}
    </ul>
  );
}

function FileLine({ file }: { file: CodeRunFile }) {
  const FileIcon = file.image ? ImageIcon : File;
  return (
    <span className="inline-flex items-center gap-1.5 text-fg-secondary">
      <FileIcon aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      {file.name}
    </span>
  );
}

const PREVIEWED = new Set(["image/png", "image/jpeg"]);

function FileCard({ file, scope }: { file: CodeRunFile; scope: ConversationScope }) {
  const { t } = useTranslation();
  const list = useArtefacts(scope);
  const { show } = useOpenArtefact();
  const titleId = useId();
  const queryClient = useQueryClient();
  const summary = list.data?.artefacts.find((a) => a.handle === file.artefact);
  const readable = summary !== undefined && summary.version >= file.version;
  // A file the cached list lacks (kept mid-turn): the list is read once more, as artefact cards
  // do, before saying it is unavailable (code review).
  const behind = list.isSuccess && !readable;
  const refetched = useRef(false);
  useEffect(() => {
    if (behind && !refetched.current) {
      refetched.current = true;
      refreshArtefacts(queryClient, scope.conversationId);
    }
  }, [behind, queryClient, scope.conversationId]);
  const settled = list.isSuccess && !list.isFetching && refetched.current;
  const FileIcon = PREVIEWED.has(file.mime) ? ImageIcon : File;
  if (behind && settled) {
    return (
      <p className="inline-flex items-center gap-1.5 text-fg-muted">
        <Ban aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        {t("artefacts.unavailable")}
      </p>
    );
  }
  return (
    <section
      aria-labelledby={titleId}
      data-esi-content="files"
      className="flex max-w-md flex-col overflow-hidden rounded-lg border border-line bg-surface-raised text-sm"
    >
      {readable && PREVIEWED.has(file.mime) && file.size <= 4 * 1024 * 1024 ? (
        <img
          src={artefactPreviewHref(scope, summary.id, file.version)}
          alt={file.name}
          loading="lazy"
          className="max-h-72 w-full border-b border-line bg-surface-raised object-contain"
        />
      ) : null}
      <div className="flex items-center gap-3 px-3 py-2.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-sunken">
          <FileIcon aria-hidden="true" className="size-4 text-fg-secondary" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <p id={titleId} className="truncate font-medium text-fg">
            {file.name}
          </p>
          <p className="text-xs text-fg-muted tabular-nums">
            {t("artefacts.kind.file")} · {formatBytes(file.size)}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!readable}
          aria-label={t("artefacts.openNamed", { title: file.name })}
          onClick={() => summary && show({ artefactId: summary.id, version: file.version })}
        >
          {t("artefacts.open")}
        </Button>
        {readable ? (
          <Button variant="outline" size="sm" asChild>
            <a
              href={artefactDownloadHref(scope, summary.id, file.version, "file")}
              download
              aria-label={t("tools.code.downloadNamed", { name: file.name })}
            >
              <Download aria-hidden="true" strokeWidth={1.75} />
              <span className="max-sm:sr-only">{t("artefacts.panel.download")}</span>
            </a>
          </Button>
        ) : null}
      </div>
    </section>
  );
}

/**
 * Asks the creator to allow a run (agent-turn D17: Restricted content read in the turn): the
 * exact program and the names of the files it would get, before anything is sent.
 */
export function CodeApprovalCard(props: {
  part: ToolPart;
  files: readonly string[];
  onAllow: () => void;
  onDecline: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const input = codeRunInputOf(props.part.input);
  return (
    <section
      aria-labelledby={titleId}
      data-code-approval=""
      className="flex flex-col gap-2.5 rounded-lg border border-line bg-surface-raised p-3.5 text-sm"
    >
      <div className="flex flex-col gap-0.5">
        <p id={titleId} className="flex items-center gap-1.5 font-semibold text-fg">
          <Code
            aria-hidden="true"
            className="size-4 shrink-0 text-fg-secondary"
            strokeWidth={1.75}
          />
          {t("tools.code.approval.title")}
        </p>
        <p className="text-fg-secondary">{t("tools.code.approval.explained")}</p>
      </div>
      {input.note ? <p className="text-[13px] text-fg-secondary">{input.note}</p> : null}
      <Block
        label={
          input.language
            ? t("tools.code.programIn", { language: input.language })
            : t("tools.code.program")
        }
        text={input.code}
      />
      {props.files.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-fg-muted">{t("tools.code.approval.files")}</span>
          <ul className="flex flex-col gap-0.5">
            {props.files.map((name, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: names may repeat; order is stable
              <li key={i} className="inline-flex items-center gap-1.5 break-words text-fg">
                <File
                  aria-hidden="true"
                  className="size-3.5 shrink-0 text-fg-muted"
                  strokeWidth={1.75}
                />
                {name}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={props.disabled} onClick={props.onAllow}>
          {t("tools.code.approval.allow")}
        </Button>
        <Button size="sm" variant="outline" disabled={props.disabled} onClick={props.onDecline}>
          {t("tools.approval.decline")}
        </Button>
      </div>
    </section>
  );
}
